export type TickType = 'price' | 'time' | 'force';

export interface TickData {
  value: number;
  label: string;
}

export interface TicksResult {
  primary: TickData[];
  secondary: TickData[];
  tertiary: TickData[];
}

const TIME_STEPS = [
  60 * 1000,               // 1m
  2 * 60 * 1000,           // 2m
  5 * 60 * 1000,           // 5m
  10 * 60 * 1000,          // 10m
  15 * 60 * 1000,          // 15m
  30 * 60 * 1000,          // 30m
  60 * 60 * 1000,          // 1h
  2 * 60 * 60 * 1000,      // 2h
  4 * 60 * 60 * 1000,      // 4h
  8 * 60 * 60 * 1000,      // 8h
  12 * 60 * 60 * 1000,     // 12h
  24 * 60 * 60 * 1000,     // 1D
  2 * 24 * 60 * 60 * 1000, // 2D
  4 * 24 * 60 * 60 * 1000, // 4D
  7 * 24 * 60 * 60 * 1000, // 1W
  14 * 24 * 60 * 60 * 1000,// 2W
  30 * 24 * 60 * 60 * 1000,// 1M
];

function calculateTimeStep(range: number, targetTicks: number): number {
  const targetStep = range / targetTicks;
  for (const step of TIME_STEPS) {
    if (step >= targetStep) {
      return step;
    }
  }
  return TIME_STEPS[TIME_STEPS.length - 1];
}

export function calculateStep(range: number, targetTicks: number): number {
  if (range <= 0 || targetTicks <= 0) return 1;
  const unroundedStepSize = range / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(unroundedStepSize)));
  const normalizedStepSize = unroundedStepSize / magnitude;
  
  let stepMultiplier;
  if (normalizedStepSize < 1.5) stepMultiplier = 1;
  else if (normalizedStepSize < 3.5) stepMultiplier = 2;
  else if (normalizedStepSize < 7.5) stepMultiplier = 5;
  else stepMultiplier = 10;
  
  return stepMultiplier * magnitude;
}

export function formatPrice(value: number, step: number): string {
  const abs = Math.abs(value);
  if (abs >= 1e6) {
    return `${(value / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `${(value / 1e3).toFixed(1)}K`;
  }
  let decimals = 2;
  if (step < 0.01) decimals = 3;
  if (step < 0.001) decimals = 4;
  return value.toFixed(decimals);
}

export function formatForce(value: number): string {
  if (value === 0) return '0';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1e9) return `${sign}${(absValue / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `${sign}${(absValue / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `${sign}${(absValue / 1e3).toFixed(1)}K`;
  return `${value.toFixed(1)}`;
}

function formatTime(value: number, step: number): string {
  const date = new Date(value);
  if (step >= 24 * 60 * 60 * 1000 * 3) {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    return `${d}/${m} ${hh}:${mm}`;
  } else {
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }
}

export function calculateSmartTicks(min: number, max: number, type: TickType, maxPrimaryTicks: number = 5): TicksResult {
  const result: TicksResult = { primary: [], secondary: [], tertiary: [] };
  const range = max - min;
  if (range <= 0) return result;

  let primaryStep = 1;
  let secondaryStep = 1;
  let tertiaryStep = 1;

  if (type === 'time') {
    primaryStep = calculateTimeStep(range, maxPrimaryTicks);
    const stepIdx = TIME_STEPS.indexOf(primaryStep);
    secondaryStep = stepIdx > 0 ? TIME_STEPS[stepIdx - 1] : primaryStep / 2;
    const secStepIdx = TIME_STEPS.indexOf(secondaryStep);
    tertiaryStep = secStepIdx > 0 ? TIME_STEPS[secStepIdx - 1] : secondaryStep / 2;
  } else {
    primaryStep = calculateStep(range, maxPrimaryTicks);
    secondaryStep = primaryStep / 2;
    tertiaryStep = secondaryStep / 2;
  }

  const generatedValues = new Set<number>();

  const addTick = (val: number, step: number, level: 'primary' | 'secondary' | 'tertiary') => {
    // Use toPrecision to avoid floating point representation issues during deduplication
    const cleanVal = Number(val.toPrecision(12));
    
    // Strict deduplication across all LODs
    if (generatedValues.has(cleanVal)) return;
    generatedValues.add(cleanVal);

    let label = '';
    switch (type) {
      case 'price': label = formatPrice(cleanVal, step); break;
      case 'force': label = formatForce(cleanVal); break;
      case 'time': label = formatTime(cleanVal, step); break;
    }
    
    result[level].push({ value: cleanVal, label });
  };

  // 1. Generate Primary Ticks
  const primaryStart = Math.ceil(min / primaryStep) * primaryStep;
  for (let val = primaryStart; val <= max; val += primaryStep) {
    addTick(val, primaryStep, 'primary');
  }

  // 2. Generate Secondary Ticks
  const secondaryStart = Math.ceil(min / secondaryStep) * secondaryStep;
  for (let val = secondaryStart; val <= max; val += secondaryStep) {
    addTick(val, secondaryStep, 'secondary');
  }

  // 3. Generate Tertiary Ticks
  const tertiaryStart = Math.ceil(min / tertiaryStep) * tertiaryStep;
  for (let val = tertiaryStart; val <= max; val += tertiaryStep) {
    addTick(val, tertiaryStep, 'tertiary');
  }

  return result;
}
